import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import { ArrowPathIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { getAccountPlanBadgeClass, getAccountPlanLabel } from '../utils/accountPlanLabel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [blockedFilter, setBlockedFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(search && { search }),
        ...(tierFilter && { tier: tierFilter }),
        ...(blockedFilter && { blocked: blockedFilter })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/accounts?${params}`);
      setAccounts(response.data.data.accounts);
      setTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/accounts/stats/summary`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching account stats:', error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, tierFilter, blockedFilter, sortBy, sortOrder]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAccounts();
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const columns = [
    { 
      key: 'name', 
      label: 'Account Name',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.is_blocked && (
            <span title="Account is blocked" className="flex-shrink-0">
              <NoSymbolIcon className="h-4 w-4 text-red-600" />
            </span>
          )}
          <div className={row.is_blocked ? 'text-red-600' : ''}>
            <div className="font-medium">{row.name || 'Unnamed Account'}</div>
            {row.is_blocked && (
              <div className="text-xs text-red-500">Blocked</div>
            )}
          </div>
        </div>
      )
    },
    { 
      key: 'tier', 
      label: 'Tier',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getAccountPlanBadgeClass(row)}`}>
          {getAccountPlanLabel(row)}
        </span>
      )
    },
    {
      key: 'trial_expires_at',
      label: 'Trial Expires',
      render: (row) => {
        if (row.tier !== 'trial' && row.tier !== 'free') {
          return <span className="text-gray-400">-</span>;
        }
        if (!row.trial_expires_at) {
          return <span className="text-gray-400 text-xs">Not started</span>;
        }
        const expiresAt = new Date(row.trial_expires_at);
        const now = new Date();
        const isExpired = row.trial_expired || expiresAt < now;
        const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        
        if (isExpired) {
          return (
            <span className="text-red-600 text-xs font-medium">
              Expired
            </span>
          );
        }
        
        return (
          <div className="flex flex-col">
            <span className={`text-xs ${daysLeft <= 3 ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </span>
            <span className="text-xs text-gray-400">
              {expiresAt.toLocaleDateString()}
            </span>
          </div>
        );
      }
    },
    { 
      key: 'member_count', 
      label: 'Members',
      render: (row) => (
        <span className="font-medium">{row.member_count || 0}</span>
      )
    },
    { 
      key: 'website_count', 
      label: 'Websites',
      render: (row) => (
        <span>{row.website_count || 0}</span>
      )
    },
    { 
      key: 'published_count', 
      label: 'Published',
      render: (row) => (
        <span className="text-green-600">{row.published_count || 0}</span>
      )
    },
    { 
      key: 'lifetime_sites_created', 
      label: 'Lifetime Sites',
      render: (row) => (
        <span className="text-gray-500">{row.lifetime_sites_created || 0}</span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link
          to={`/accounts/${row.id}`}
          className="text-primary-600 hover:text-primary-900"
        >
          View
        </Link>
      )
    }
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Accounts Management</h1>
          <button
            onClick={() => { fetchAccounts(); fetchStats(); }}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Accounts</div>
              <div className="text-2xl font-bold text-gray-900">{stats.totals.accounts}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Users with Accounts</div>
              <div className="text-2xl font-bold text-gray-900">{stats.totals.usersWithAccounts}</div>
              {stats.totals.usersWithoutAccounts > 0 && (
                <div className="text-xs text-amber-600">{stats.totals.usersWithoutAccounts} orphaned</div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Websites</div>
              <div className="text-2xl font-bold text-gray-900">{stats.totals.websites}</div>
              <div className="text-xs text-green-600">{stats.totals.publishedWebsites} published</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Tier Distribution</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {stats.tierDistribution.map((t) => (
                  <span key={t.tier} className={`px-2 py-0.5 text-xs rounded-full ${getAccountPlanBadgeClass(t.tier)}`}>
                    {getAccountPlanLabel(t.tier)}: {t.count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                placeholder="Search by account name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-field flex-1"
              />
              <button type="submit" className="btn-primary">Search</button>
            </form>
            
            <select
              value={tierFilter}
              onChange={(e) => {
                setTierFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field"
            >
              <option value="">All Tiers</option>
              <option value="growth">Growth</option>
              <option value="business">Business</option>
              <option value="scale">Scale</option>
              <option value="trial">Trial</option>
              <option value="pro">Pro</option>
              <option value="ecommerce">E-commerce</option>
              <option value="landing_page">Landing Page</option>
              <option value="enterprise">Enterprise</option>
            </select>
            
            <select
              value={blockedFilter}
              onChange={(e) => {
                setBlockedFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field"
            >
              <option value="">All Accounts</option>
              <option value="true">Blocked Only</option>
              <option value="false">Active Only</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={accounts}
            loading={loading}
            emptyMessage="No accounts found"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Accounts;

